import type { AttachmentId, FileClassificationStatus } from "@inrev/common";
import { FileUploadStatus, resolveFileUploadStatus } from "@portal/api/files";
import type { FC } from "react";
import { cn } from "../../lib/utils";
import { DOCUMENT_ACCEPT, DROPZONE_SM_HEIGHT_CLASS, Dropzone } from "../../patterns/dropzone";
import { FileRow } from "../../patterns/file-row";
import { Card, ClickableIcon, Spinner, Text } from "../../primitives";
import { FileUploadStatusIndicator } from "../file-upload";

export type KeyDocumentFile = {
	id: AttachmentId;
	/** Display name including extension. */
	name: string;
	extension: string;
	deleting?: boolean;
	classificationStatus?: FileClassificationStatus;
	typesConfirmedByAdmin?: boolean;
	/** Opens the original file in the PDF viewer. Omit for non-previewable files. */
	onPreview?: () => void;
	onDownload?: () => void;
	/** Opens the generated summary. Omit when no summary exists. */
	onViewSummary?: () => void;
	summaryLoading?: boolean;
	onRemove?: () => void;
};

const FILE_ACTION_CLASS = "rounded p-1 transition-colors hover:bg-surface-neutral";

export type KeyDocumentCardProps = {
	testId: string;
	/** Document role, e.g. "Contract" or "RFP / Bid Package". */
	label: string;
	files?: KeyDocumentFile[];
	uploading?: boolean;
	/** Omit to render the empty state as a disabled drop target. */
	onUpload?: (files: File[]) => void;
	/** Hint shown in the empty state, e.g. "Upload a copy of the contract". */
	emptyMessage?: string;
};

const KeyDocumentFileStatus: FC<{ file: KeyDocumentFile }> = ({ file }) => {
	const status = resolveFileUploadStatus({
		classificationStatus: file.classificationStatus,
		hasTypes: true,
		typesConfirmedByAdmin: file.typesConfirmedByAdmin ?? false,
	});
	return status === FileUploadStatus.ready ? null : (
		<FileUploadStatusIndicator status={status} id={String(file.id)} />
	);
};

export const KeyDocumentCard: FC<KeyDocumentCardProps> = ({
	testId,
	label,
	files = [],
	uploading = false,
	onUpload,
	emptyMessage,
}) => {
	const hasFiles = files.length > 0;

	return (
		<Card data-testid={testId} elevation="raised" className="flex flex-col">
			<div className="flex items-center justify-between gap-3 px-4 pt-3.5 pb-2">
				<Text type="caption" color="tertiary" className="font-semibold uppercase tracking-[0.6px]">
					{label}
				</Text>
			</div>

			<div className="flex flex-col gap-3 px-4 pb-4">
				{hasFiles && (
					<div className="flex flex-col gap-2" data-testid={`${testId}-files`}>
						{files.map((file) => (
							<FileRow
								key={file.id}
								testId={`${testId}-file-${file.id}`}
								name={file.name}
								dataStatus={file.deleting ? "deleting" : "uploaded"}
								removeAriaLabel={`Remove ${file.name}`}
								onRemove={file.deleting ? undefined : file.onRemove}
								status={
									file.deleting ? <Spinner size="sm" /> : <KeyDocumentFileStatus file={file} />
								}
								actions={
									file.deleting ? undefined : (
										<>
											{file.onPreview && (
												<ClickableIcon
													data-testid={`${testId}-preview-${file.id}`}
													type="eye"
													size="mini"
													color="tertiary"
													aria-label={`Preview ${file.name}`}
													className={FILE_ACTION_CLASS}
													onClick={file.onPreview}
												/>
											)}
											{file.onDownload && (
												<ClickableIcon
													data-testid={`${testId}-download-${file.id}`}
													type="arrow-down-tray"
													size="mini"
													color="tertiary"
													aria-label={`Download ${file.name}`}
													className={FILE_ACTION_CLASS}
													onClick={file.onDownload}
												/>
											)}
											{file.summaryLoading ? (
												<div
													className={FILE_ACTION_CLASS}
													data-testid={`${testId}-summary-loading-${file.id}`}
													aria-label={`Generating summary of ${file.name}`}
												>
													<Spinner size="sm" />
												</div>
											) : (
												file.onViewSummary && (
													<ClickableIcon
														data-testid={`${testId}-view-summary-${file.id}`}
														type="document-chart-bar"
														size="mini"
														color="tertiary"
														aria-label={`View summary of ${file.name}`}
														className={FILE_ACTION_CLASS}
														onClick={file.onViewSummary}
													/>
												)
											)}
										</>
									)
								}
							/>
						))}
					</div>
				)}

				{uploading && (
					<div
						className={cn("flex items-center justify-center gap-2", DROPZONE_SM_HEIGHT_CLASS)}
						data-testid={`${testId}-uploading`}
					>
						<Spinner size="sm" />
						<Text type="caption" color="tertiary">
							Uploading...
						</Text>
					</div>
				)}

				{!uploading && (!hasFiles || onUpload !== undefined) && (
					<Dropzone
						testId={`${testId}-dropzone`}
						onDrop={onUpload ?? noop}
						accept={DOCUMENT_ACCEPT}
						disabled={onUpload === undefined}
						size="sm"
						placeholder={
							hasFiles
								? `Upload another ${label.toLowerCase()}`
								: (emptyMessage ?? `Upload ${label}`)
						}
						caption={null}
					/>
				)}
			</div>
		</Card>
	);
};

const noop = () => {};
